import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../../../services/firebase";
import { COLLECTIONS } from "../../../../services/collections";
import { toast } from "react-toastify";

const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function isCurrentMonth(period) {
    if (!period || period === "custom") return false;
    const now = new Date();
    const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    return period === currentPeriod;
}

function isCacheStale(snapshots) {
    if (!snapshots || snapshots.length === 0) return true;

    let latest = null;
    for (const snap of snapshots) {
        const genAt = snap.generatedAt;
        if (!genAt) continue;

        let dateMs;
        if (typeof genAt.toDate === "function") {
            dateMs = genAt.toDate().getTime();
        } else if (genAt.seconds) {
            dateMs = genAt.seconds * 1000;
        } else {
            continue;
        }

        if (!latest || dateMs > latest) {
            latest = dateMs;
        }
    }

    if (!latest) return true;
    return (Date.now() - latest) > CACHE_TTL_MS;
}

export const usePerformanceData = ({ idTenant, selectedPeriod, startDate, endDate }) => {
    const autoRecalcDone = useRef(false);

    const [snapshots, setSnapshots] = useState([]);
    const [excludedSnapshots, setExcludedSnapshots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [recalculating, setRecalculating] = useState(false);

    // ── Período atual vs passado ──
    const isCurrentPeriod = useMemo(() => {
        if (!selectedPeriod || selectedPeriod === "custom") return true; // custom sempre permite
        return isCurrentMonth(selectedPeriod);
    }, [selectedPeriod]);

    const fetchSnapshots = useCallback(async () => {
        if (!idTenant || !selectedPeriod) return;
        setLoading(true);

        try {
            let queryPeriod = selectedPeriod;
            if (selectedPeriod === "custom") {
                if (!startDate || !endDate) {
                    setSnapshots([]);
                    setLoading(false);
                    return;
                }
                queryPeriod = `${startDate}_${endDate}`;
            }

            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.PERFORMANCE_SNAPSHOTS),
                where("period", "==", queryPeriod),
            );

            const snap = await getDocs(q);
            const allData = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            // Separar: ranking válido vs dados insuficientes
            const validData = allData
                .filter(d => !d.insufficientData)
                .sort((a, b) => {
                    const scoreA = a.scoreFinal || 0;
                    const scoreB = b.scoreFinal || 0;
                    if (scoreB !== scoreA) {
                        return scoreB - scoreA;
                    }
                    const membersA = a.metrics?.raw?.totalEnrolledCount || 0;
                    const membersB = b.metrics?.raw?.totalEnrolledCount || 0;
                    return membersB - membersA;
                });
            const excludedData = allData.filter(d => d.insufficientData === true);

            setSnapshots(validData);
            setExcludedSnapshots(excludedData);
            return validData;
        } catch (error) {
            console.error("[usePerformanceData] Erro ao buscar snapshots:", error);
            toast.error("Erro ao carregar dados de performance.");
            return [];
        } finally {
            setLoading(false);
        }
    }, [idTenant, selectedPeriod, startDate, endDate]);

    const autoRecalculate = useCallback(async (periodToCall) => {
        if (!idTenant || recalculating) return;

        setRecalculating(true);
        try {
            const calculateFn = httpsCallable(functions, "calculatePerformanceSnapshots");
            await calculateFn({ idTenant, period: periodToCall });
            await fetchSnapshots();
        } catch (error) {
            console.error("[usePerformanceData] Auto-recalc falhou:", error);
        } finally {
            setRecalculating(false);
        }
    }, [idTenant, recalculating, fetchSnapshots]);

    useEffect(() => {
        autoRecalcDone.current = false;
    }, [selectedPeriod]);

    useEffect(() => {
        const load = async () => {
            const data = await fetchSnapshots();

            if (
                idTenant &&
                isCurrentMonth(selectedPeriod) &&
                !autoRecalcDone.current &&
                isCacheStale(data)
            ) {
                autoRecalcDone.current = true;
                autoRecalculate(selectedPeriod);
            }
        };
        load();
    }, [fetchSnapshots, selectedPeriod, autoRecalculate, idTenant]);

    const handleRecalculate = async () => {
        if (!idTenant) return;
        
        let periodToCall = selectedPeriod;
        if (selectedPeriod === "custom") {
            if (!startDate || !endDate) {
                return toast.error("Selecione a data de início e fim no filtro.");
            }
            if (new Date(startDate) > new Date(endDate)) {
                return toast.error("A data de início deve ser anterior à data de fim.");
            }
            periodToCall = `${startDate}_${endDate}`;
        }

        setRecalculating(true);

        try {
            const calculateFn = httpsCallable(functions, "calculatePerformanceSnapshots");
            const result = await calculateFn({ idTenant, period: periodToCall });

            if (result.data?.skipped) {
                toast.info(result.data.message || "Este período já possui snapshots imutáveis.");
            } else {
                toast.success(`✅ ${result.data.snapshots} colaboradores avaliados!`);
            }
            await fetchSnapshots(); 
        } catch (error) {
            console.error("[usePerformanceData] Erro ao recalcular:", error);
            toast.error(error?.message || "Erro ao calcular performance.");
        } finally {
            setRecalculating(false);
        }
    };

    return {
        snapshots,
        excludedSnapshots,
        loading,
        recalculating,
        isCurrentPeriod,
        handleRecalculate
    };
};
